import React,{useState,useEffect,useCallback} from 'react';
import {currencyAPI,branchAPI} from '../utils/api';
import {PageHeader,DataTable,Badge,Modal,FormInput,fmt} from '../components/common';
import {useLang} from '../i18n';
import {useAuth} from '../context/AuthContext';
import toast from 'react-hot-toast';
import {Plus,Edit,Trash2,RefreshCw,TrendingUp,TrendingDown,Minus,Globe,Building2,Lock,CheckCircle,AlertCircle} from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────────
const today=()=>new Date().toISOString().slice(0,10);
const fmtRate=v=>v!=null?parseFloat(v).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:4}):'—';
const fmtDate=d=>{if(!d)return'—';try{return new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'})}catch{return d}};

function RateChange({current,previous}){
  if(!previous||!current) return null;
  const diff=((current-previous)/previous*100).toFixed(2);
  const up=current>previous;
  const same=current===previous;
  if(same) return <span style={{color:'var(--td)',fontSize:11,display:'flex',alignItems:'center',gap:2}}><Minus size={10}/>0%</span>;
  return(
    <span style={{color:up?'var(--success)':'var(--danger)',fontSize:11,display:'flex',alignItems:'center',gap:2}}>
      {up?<TrendingUp size={10}/>:<TrendingDown size={10}/>}{up?'+':''}{diff}%
    </span>
  );
}

// ── Rate Form Modal ───────────────────────────────────────────────
function RateModal({open,onClose,onSave,currencies,initial,title}){
  const{t}=useLang();
  const[form,setForm]=useState({from_currency:'USD',to_currency:'KHR',rate:'',effective_date:today(),notes:''});
  useEffect(()=>{if(initial)setForm({from_currency:initial.from_currency||'USD',to_currency:initial.to_currency||'KHR',rate:String(initial.rate||''),effective_date:initial.effective_date||today(),notes:initial.notes||''});else setForm({from_currency:'USD',to_currency:'KHR',rate:'',effective_date:today(),notes:''});},[initial,open]);
  const F=(k,v)=>setForm(f=>({...f,[k]:v}));
  const currOpts=currencies.map(c=>({value:c.code,label:`${c.code} — ${c.name} (${c.symbol})`}));
  const save=()=>{
    if(!form.rate||isNaN(parseFloat(form.rate))||parseFloat(form.rate)<=0) return toast.error('Valid rate required');
    if(form.from_currency===form.to_currency) return toast.error('From and To currencies must differ');
    onSave({...form,rate:parseFloat(form.rate)});
  };
  return(
    <Modal open={open} onClose={onClose} title={title} width={480}>
      <div className="form-grid">
        <FormInput label={t('currencies.fromCurrency')} value={form.from_currency} onChange={v=>F('from_currency',v)} options={currOpts}/>
        <FormInput label={t('currencies.toCurrency')} value={form.to_currency} onChange={v=>F('to_currency',v)} options={currOpts}/>
      </div>
      <div style={{padding:'10px 14px',background:'var(--s2)',borderRadius:8,marginBottom:14,textAlign:'center'}}>
        <div style={{fontSize:11,color:'var(--td)',marginBottom:4}}>Preview</div>
        <div style={{fontSize:18,fontWeight:700,color:'var(--primary)'}}>
          1 {form.from_currency} = {form.rate||'?'} {form.to_currency}
        </div>
      </div>
      <FormInput label={t('currencies.rate')+' *'} value={form.rate} onChange={v=>F('rate',v)} type="number" placeholder="e.g. 4100"/>
      <FormInput label={t('currencies.effectiveDate')} value={form.effective_date} onChange={v=>F('effective_date',v)} type="date"/>
      <FormInput label={t('currencies.notes')} value={form.notes} onChange={v=>F('notes',v)} placeholder={t('currencies.notesPlaceholder')}/>
      <div className="modal-footer">
        <button className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
        <button className="btn btn-primary" onClick={save}>{t('common.save')}</button>
      </div>
    </Modal>
  );
}

// ── Branch Rate Form Modal ────────────────────────────────────────
function BranchRateModal({open,onClose,onSave,currencies,branches,initial,title,isOwner,myBranchId}){
  const{t}=useLang();
  const[form,setForm]=useState({branch_id:'',from_currency:'USD',to_currency:'KHR',rate:'',effective_date:today(),notes:'',is_active:true});
  useEffect(()=>{
    if(initial) setForm({branch_id:String(initial.branch_id||''),from_currency:initial.from_currency||'USD',to_currency:initial.to_currency||'KHR',rate:String(initial.rate||''),effective_date:initial.effective_date||today(),notes:initial.notes||'',is_active:initial.is_active!==false});
    else setForm({branch_id:isOwner?'':String(myBranchId||''),from_currency:'USD',to_currency:'KHR',rate:'',effective_date:today(),notes:'',is_active:true});
  },[initial,open,isOwner,myBranchId]);
  const F=(k,v)=>setForm(f=>({...f,[k]:v}));
  const currOpts=currencies.map(c=>({value:c.code,label:`${c.code} — ${c.name} (${c.symbol})`}));
  const branchOpts=[{value:'',label:`— ${t('common.all')} —`},...branches.map(b=>({value:String(b.id),label:`${b.code} · ${b.name}`}))];
  const save=()=>{
    if(!form.branch_id) return toast.error('Branch is required');
    if(!form.rate||isNaN(parseFloat(form.rate))||parseFloat(form.rate)<=0) return toast.error('Valid rate required');
    if(form.from_currency===form.to_currency) return toast.error('From and To currencies must differ');
    onSave({...form,branch_id:parseInt(form.branch_id),rate:parseFloat(form.rate)});
  };
  return(
    <Modal open={open} onClose={onClose} title={title} width={500}>
      {!isOwner&&myBranchId&&(
        <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',background:'rgba(0,212,170,0.08)',border:'1px solid rgba(0,212,170,0.2)',borderRadius:8,marginBottom:14,fontSize:12}}>
          <Lock size={13} color="var(--primary)"/><span style={{color:'var(--primary)'}}>{t('currencies.branch')}: <strong>{branches.find(b=>b.id===myBranchId)?.name||myBranchId}</strong></span>
        </div>
      )}
      {isOwner&&<FormInput label={t('currencies.branch')+' *'} value={form.branch_id} onChange={v=>F('branch_id',v)} options={branchOpts}/>}
      <div className="form-grid">
        <FormInput label={t('currencies.fromCurrency')} value={form.from_currency} onChange={v=>F('from_currency',v)} options={currOpts}/>
        <FormInput label={t('currencies.toCurrency')} value={form.to_currency} onChange={v=>F('to_currency',v)} options={currOpts}/>
      </div>
      <div style={{padding:'10px 14px',background:'var(--s2)',borderRadius:8,marginBottom:14,textAlign:'center'}}>
        <div style={{fontSize:11,color:'var(--td)',marginBottom:4}}>Preview</div>
        <div style={{fontSize:18,fontWeight:700,color:'var(--primary)'}}>
          1 {form.from_currency} = {form.rate||'?'} {form.to_currency}
        </div>
      </div>
      <FormInput label={t('currencies.rate')+' *'} value={form.rate} onChange={v=>F('rate',v)} type="number" placeholder="e.g. 4100"/>
      <FormInput label={t('currencies.effectiveDate')} value={form.effective_date} onChange={v=>F('effective_date',v)} type="date"/>
      <FormInput label={t('currencies.notes')} value={form.notes} onChange={v=>F('notes',v)} placeholder={t('currencies.notesPlaceholder')}/>
      <FormInput label={t('currencies.activateLabel')} value={form.is_active?'true':'false'} onChange={v=>F('is_active',v==='true')} options={[{value:'true',label:t('common.active')},{value:'false',label:t('common.inactive')}]}/>
      <div className="modal-footer">
        <button className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
        <button className="btn btn-primary" onClick={save}>{t('common.save')}</button>
      </div>
    </Modal>
  );
}

// ── Currency Card (compact overview per pair) ─────────────────────
function RateSummaryCard({pair,globalRate,branchRates,currencies}){
  const{t}=useLang();
  const fromCurr=currencies.find(c=>c.code===pair.from);
  const toCurr=currencies.find(c=>c.code===pair.to);
  const latestGlobal=globalRate?parseFloat(globalRate.rate):null;
  const branchCount=branchRates.length;
  return(
    <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,padding:16,display:'flex',flexDirection:'column',gap:10}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <div style={{background:'var(--s2)',padding:'4px 10px',borderRadius:6,fontFamily:'monospace',fontWeight:700,color:'var(--primary)',fontSize:13}}>{pair.from}</div>
          <span style={{color:'var(--td)'}}>→</span>
          <div style={{background:'var(--s2)',padding:'4px 10px',borderRadius:6,fontFamily:'monospace',fontWeight:700,color:'var(--tm)',fontSize:13}}>{pair.to}</div>
        </div>
        <div style={{fontSize:11,color:'var(--td)'}}>{fromCurr?.name} → {toCurr?.name}</div>
      </div>
      <div style={{display:'flex',gap:12,alignItems:'center'}}>
        <div style={{flex:1,background:'var(--s2)',borderRadius:8,padding:'10px 12px',textAlign:'center'}}>
          <div style={{fontSize:10,color:'var(--td)',marginBottom:3,display:'flex',alignItems:'center',gap:4,justifyContent:'center'}}><Globe size={10}/>{t('currencies.tabRates')}</div>
          <div style={{fontSize:18,fontWeight:700,color:'var(--text)'}}>{latestGlobal?fmtRate(latestGlobal):<span style={{color:'var(--td)',fontSize:13}}>—</span>}</div>
          {globalRate&&<div style={{fontSize:10,color:'var(--td)',marginTop:2}}>{fmtDate(globalRate.effective_date)}</div>}
        </div>
        <div style={{flex:1,background:'var(--s2)',borderRadius:8,padding:'10px 12px',textAlign:'center'}}>
          <div style={{fontSize:10,color:'var(--td)',marginBottom:3,display:'flex',alignItems:'center',gap:4,justifyContent:'center'}}><Building2 size={10}/>{t('currencies.tabBranchRates')}</div>
          {branchCount>0?(
            <div>
              <div style={{fontSize:16,fontWeight:700,color:'var(--primary)'}}>{branchCount}</div>
              <div style={{fontSize:10,color:'var(--td)',marginTop:2}}>{t('nav.branches')}</div>
            </div>
          ):(
            <div style={{fontSize:12,color:'var(--td)'}}>{t('currencies.usesGlobalRate')}</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────
export default function CurrenciesPage(){
  const{t}=useLang();
  const{isOwner,myBranchId,hasPermission}=useAuth();
  const canManage=isOwner||hasPermission('currency.manage');

  const[currencies,setCurrencies]=useState([]);
  const[rates,setRates]=useState([]);
  const[branchRates,setBranchRates]=useState([]);
  const[branches,setBranches]=useState([]);
  const[ld,setLd]=useState(true);
  const[tab,setTab]=useState('currencies');
  const[filterBranch,setFilterBranch]=useState('');
  const[filterCurrency,setFilterCurrency]=useState('');

  // Modals
  const[rateModal,setRateModal]=useState(null); // null | 'new' | {id,...}
  const[branchRateModal,setBranchRateModal]=useState(null);
  const[currencyModal,setCurrencyModal]=useState(null);
  const[deleteTarget,setDeleteTarget]=useState(null); // {type:'rate'|'branch_rate', id}
  const[currForm,setCurrForm]=useState({code:'',name:'',symbol:'',is_default:false,is_active:true});

  const load=useCallback(async()=>{
    setLd(true);
    try{
      const[c,r,br,b]=await Promise.all([
        currencyAPI.getAll(),
        currencyAPI.getRates(),
        currencyAPI.getBranchRates(),
        branchAPI.getAll({limit:100}),
      ]);
      setCurrencies(c.data.data||c.data||[]);
      setRates(r.data.data||r.data||[]);
      setBranchRates(br.data.data||br.data||[]);
      setBranches(b.data.data||b.data||[]);
    }catch{toast.error(t('common.failedToLoad'));}
    finally{setLd(false);}
  },[]);

  useEffect(()=>{load();},[load]);

  // Currency pairs from global rates
  const pairs=[...new Set(rates.map(r=>`${r.from_currency}→${r.to_currency}`))].map(p=>{
    const[from,to]=p.split('→');
    const pairRates=rates.filter(r=>r.from_currency===from&&r.to_currency===to).sort((a,b)=>new Date(b.effective_date)-new Date(a.effective_date));
    const pairBranch=branchRates.filter(r=>r.from_currency===from&&r.to_currency===to&&r.is_active);
    return{from,to,latest:pairRates[0],previous:pairRates[1],branchRates:pairBranch};
  });

  // Save global rate
  const saveRate=async(form)=>{
    try{
      if(rateModal==='new'){await currencyAPI.createRate(form);toast.success(t('currencies.rateCreated'));}
      else{await currencyAPI.updateRate(rateModal.id,form);toast.success(t('currencies.rateUpdated'));}
      setRateModal(null);load();
    }catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'));}
  };

  // Save branch rate
  const saveBranchRate=async(form)=>{
    try{
      if(branchRateModal==='new'){await currencyAPI.createBranchRate(form);toast.success(t('currencies.branchRateCreated'));}
      else{await currencyAPI.updateBranchRate(branchRateModal.id,form);toast.success(t('currencies.branchRateUpdated'));}
      setBranchRateModal(null);load();
    }catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'));}
  };

  // Delete
  const confirmDelete=async()=>{
    if(!deleteTarget)return;
    try{
      if(deleteTarget.type==='rate'){await currencyAPI.deleteRate(deleteTarget.id);toast.success(t('currencies.rateDeleted'));}
      else{await currencyAPI.deleteBranchRate(deleteTarget.id);toast.success(t('currencies.branchRateDeleted'));}
      setDeleteTarget(null);load();
    }catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'));}
  };

  // Currency update (toggle default/active)
  const saveCurrency=async()=>{
    try{
      await currencyAPI.update(currencyModal.id,{name:currForm.name,symbol:currForm.symbol,is_default:currForm.is_default,is_active:currForm.is_active});
      toast.success(t('currencies.currencyUpdated'));setCurrencyModal(null);load();
    }catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'));}
  };

  // Filter branch rates
  const filteredBranchRates=branchRates.filter(r=>{
    if(filterBranch&&String(r.branch_id)!==filterBranch) return false;
    if(filterCurrency&&r.from_currency!==filterCurrency&&r.to_currency!==filterCurrency) return false;
    return true;
  });

  // Group branch rates by branch for the card view
  const branchGroups=[...new Set(branchRates.map(r=>r.branch_id))].map(bid=>{
    const bRates=branchRates.filter(r=>r.branch_id===bid);
    const branch=bRates[0]?.branch;
    return{branch_id:bid,branch,rates:bRates};
  });

  const branchOpts=[{value:'',label:t('currencies.allBranches')},...branches.map(b=>({value:String(b.id),label:`${b.code} · ${b.name}`}))];
  const currOpts=[{value:'',label:t('common.all')},...currencies.map(c=>({value:c.code,label:`${c.code} — ${c.name}`}))];

  const tabs=[
    {key:'currencies',label:t('currencies.tabCurrencies'),icon:<span style={{fontSize:16}}>💱</span>},
    {key:'rates',label:t('currencies.tabRates'),icon:<Globe size={14}/>},
    {key:'branchRates',label:t('currencies.tabBranchRates'),icon:<Building2 size={14}/>},
  ];

  return(
    <div>
      <PageHeader
        title={t('currencies.title')}
        subtitle={t('currencies.subtitle',{c:currencies.length,r:rates.length,b:branchRates.length})}
        action={canManage&&(
          <div style={{display:'flex',gap:8}}>
            {tab==='rates'&&<button className="btn btn-primary" onClick={()=>setRateModal('new')}><Plus size={15}/> {t('currencies.addRate')}</button>}
            {tab==='branchRates'&&<button className="btn btn-primary" onClick={()=>setBranchRateModal('new')}><Plus size={15}/> {t('currencies.addBranchRate')}</button>}
          </div>
        )}
      />

      {/* Tab bar */}
      <div style={{display:'flex',gap:2,marginBottom:20,borderBottom:'1px solid var(--border)',paddingBottom:0}}>
        {tabs.map(({key,label,icon})=>(
          <button key={key} onClick={()=>setTab(key)} style={{display:'flex',alignItems:'center',gap:6,padding:'10px 18px',background:'none',border:'none',cursor:'pointer',fontWeight:tab===key?700:400,color:tab===key?'var(--primary)':'var(--td)',borderBottom:tab===key?'2px solid var(--primary)':'2px solid transparent',fontSize:13,transition:'all .15s'}}>
            {icon}{label}
          </button>
        ))}
        <div style={{flex:1}}/>
        <button className="btn btn-ghost btn-sm" onClick={load} style={{marginBottom:4}}><RefreshCw size={13}/></button>
      </div>

      {/* ── CURRENCIES TAB ── */}
      {tab==='currencies'&&(
        <div>
          {/* Overview cards */}
          {pairs.length>0&&(
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:12,marginBottom:20}}>
              {pairs.map(p=>(
                <RateSummaryCard key={`${p.from}-${p.to}`} pair={p} globalRate={p.latest} branchRates={p.branchRates} currencies={currencies}/>
              ))}
            </div>
          )}
          <DataTable loading={ld} columns={[
            {key:'code',label:t('currencies.columns.code'),render:r=><span className="font-bold text-primary" style={{fontFamily:'monospace',fontSize:13}}>{r.code}</span>},
            {key:'name',label:t('currencies.columns.name'),render:r=><span style={{fontWeight:500}}>{r.name}</span>},
            {key:'symbol',label:t('currencies.columns.symbol'),render:r=><span style={{fontSize:20,fontWeight:700,color:'var(--tm)'}}>{r.symbol}</span>},
            {key:'is_default',label:t('currencies.columns.default'),render:r=>r.is_default?<Badge variant="accent">{t('currencies.defaultLabel')}</Badge>:<span className="text-dim">—</span>},
            {key:'is_active',label:t('currencies.columns.status'),render:r=><Badge variant={r.is_active?'success':'danger'}>{r.is_active?t('common.active'):t('common.inactive')}</Badge>},
          ]} data={currencies} actions={canManage?r=>(
            <button className="btn btn-ghost btn-sm" onClick={()=>{setCurrencyModal(r);setCurrForm({name:r.name,symbol:r.symbol,is_default:r.is_default,is_active:r.is_active});}}><Edit size={13}/></button>
          ):undefined}/>
        </div>
      )}

      {/* ── GLOBAL RATES TAB ── */}
      {tab==='rates'&&(
        <div>
          <div style={{padding:'10px 14px',background:'var(--s2)',borderRadius:8,border:'1px solid var(--border)',marginBottom:16,fontSize:12,color:'var(--td)',display:'flex',alignItems:'center',gap:8}}>
            <Globe size={14} color="var(--primary)"/>{t('currencies.globalRatesDesc')}
          </div>
          <DataTable loading={ld} columns={[
            {key:'pair',label:t('currencies.fromCurrency')+' / '+t('currencies.toCurrency'),render:r=>(
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span style={{fontFamily:'monospace',fontWeight:700,color:'var(--primary)',background:'var(--s2)',padding:'2px 8px',borderRadius:4}}>{r.from_currency}</span>
                <span style={{color:'var(--td)',fontSize:11}}>→</span>
                <span style={{fontFamily:'monospace',fontWeight:700,color:'var(--tm)',background:'var(--s2)',padding:'2px 8px',borderRadius:4}}>{r.to_currency}</span>
              </div>
            )},
            {key:'rate',label:t('currencies.rate'),render:r=>(
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span style={{fontSize:16,fontWeight:700,color:'var(--text)'}}>{fmtRate(r.rate)}</span>
                {(() => {
                  const prev=rates.filter(x=>x.from_currency===r.from_currency&&x.to_currency===r.to_currency&&x.id<r.id).sort((a,b)=>b.id-a.id)[0];
                  return prev?<RateChange current={parseFloat(r.rate)} previous={parseFloat(prev.rate)}/>:null;
                })()}
              </div>
            )},
            {key:'effective_date',label:t('currencies.effectiveDate'),render:r=><span style={{fontSize:12}}>{fmtDate(r.effective_date)}</span>},
            {key:'notes',label:t('currencies.notes'),render:r=><span style={{color:'var(--td)',fontSize:12}}>{r.notes||'—'}</span>},
          ]} data={rates} actions={canManage?r=>(
            <div style={{display:'flex',gap:6}}>
              <button className="btn btn-ghost btn-sm" onClick={()=>setRateModal(r)}><Edit size={13}/></button>
              <button className="btn btn-danger btn-sm" onClick={()=>setDeleteTarget({type:'rate',id:r.id})}><Trash2 size={13}/></button>
            </div>
          ):undefined}/>
          {rates.length===0&&!ld&&<p className="text-dim" style={{textAlign:'center',padding:24}}>{t('currencies.noRates')}</p>}
        </div>
      )}

      {/* ── BRANCH RATES TAB ── */}
      {tab==='branchRates'&&(
        <div>
          <div style={{padding:'10px 14px',background:'var(--s2)',borderRadius:8,border:'1px solid var(--border)',marginBottom:16,fontSize:12,color:'var(--td)',display:'flex',alignItems:'center',gap:8}}>
            <Building2 size={14} color="var(--primary)"/>{t('currencies.branchRatesDesc')}
          </div>

          {/* Filters */}
          <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap'}}>
            <div style={{minWidth:200}}>
              <select className="form-select" value={filterBranch} onChange={e=>setFilterBranch(e.target.value)} style={{width:'100%'}}>
                {branchOpts.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div style={{minWidth:180}}>
              <select className="form-select" value={filterCurrency} onChange={e=>setFilterCurrency(e.target.value)} style={{width:'100%'}}>
                {currOpts.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {(filterBranch||filterCurrency)&&<button className="btn btn-ghost btn-sm" onClick={()=>{setFilterBranch('');setFilterCurrency('');}}>✕ {t('common.clear')}</button>}
          </div>

          {/* Branch grouping cards — shown when no filter */}
          {!filterBranch&&!filterCurrency&&branchGroups.length>0&&(
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))',gap:12,marginBottom:20}}>
              {branchGroups.map(({branch_id,branch,rates:bRates})=>{
                // find global equivalents for comparison
                const activeRates=bRates.filter(r=>r.is_active);
                return(
                  <div key={branch_id} style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,overflow:'hidden'}}>
                    <div style={{padding:'12px 16px',background:'var(--s2)',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <Building2 size={15} color="var(--primary)"/>
                        <span style={{fontWeight:700,fontSize:13}}>{branch?.name||`Branch ${branch_id}`}</span>
                        <span style={{fontFamily:'monospace',fontSize:11,color:'var(--td)',background:'var(--border)',padding:'1px 6px',borderRadius:4}}>{branch?.code||''}</span>
                      </div>
                      <Badge variant="info" style={{fontSize:11}}>{activeRates.length} {t('nav.currencies')}</Badge>
                    </div>
                    <div style={{padding:12,display:'flex',flexDirection:'column',gap:8}}>
                      {activeRates.map((r,i)=>{
                        const globalMatch=rates.filter(g=>g.from_currency===r.from_currency&&g.to_currency===r.to_currency).sort((a,b)=>new Date(b.effective_date)-new Date(a.effective_date))[0];
                        const diff=globalMatch?((parseFloat(r.rate)-parseFloat(globalMatch.rate))/parseFloat(globalMatch.rate)*100).toFixed(2):null;
                        return(
                          <div key={i} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'8px 10px',background:'var(--s2)',borderRadius:8}}>
                            <div style={{display:'flex',alignItems:'center',gap:8}}>
                              <span style={{fontFamily:'monospace',fontSize:12,color:'var(--primary)',fontWeight:700}}>{r.from_currency}</span>
                              <span style={{color:'var(--td)',fontSize:10}}>→</span>
                              <span style={{fontFamily:'monospace',fontSize:12,fontWeight:700}}>{r.to_currency}</span>
                            </div>
                            <div style={{textAlign:'right'}}>
                              <div style={{fontWeight:700,fontSize:14}}>{fmtRate(r.rate)}</div>
                              {diff!==null&&(
                                <div style={{fontSize:10,color:parseFloat(diff)>0?'var(--warning)':parseFloat(diff)<0?'var(--primary)':'var(--td)'}}>
                                  {parseFloat(diff)>0?'↑':'↓'}{Math.abs(diff)}% vs global
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {activeRates.length===0&&<p style={{color:'var(--td)',fontSize:12,textAlign:'center',padding:8}}>{t('currencies.usesGlobalRate')}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Full table */}
          <DataTable loading={ld} columns={[
            {key:'branch',label:t('currencies.columns.branch'),render:r=>(
              <div style={{display:'flex',alignItems:'center',gap:6}}>
                <Building2 size={13} color="var(--primary)"/>
                <div><div style={{fontWeight:600,fontSize:13}}>{r.branch?.name||`#${r.branch_id}`}</div><div style={{fontSize:10,color:'var(--td)',fontFamily:'monospace'}}>{r.branch?.code||''}</div></div>
              </div>
            )},
            {key:'pair',label:t('currencies.fromCurrency')+' / '+t('currencies.toCurrency'),render:r=>(
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span style={{fontFamily:'monospace',fontWeight:700,color:'var(--primary)',background:'var(--s2)',padding:'2px 8px',borderRadius:4}}>{r.from_currency}</span>
                <span style={{color:'var(--td)',fontSize:11}}>→</span>
                <span style={{fontFamily:'monospace',fontWeight:700,color:'var(--tm)',background:'var(--s2)',padding:'2px 8px',borderRadius:4}}>{r.to_currency}</span>
              </div>
            )},
            {key:'rate',label:t('currencies.rate'),render:r=>{
              const globalMatch=rates.filter(g=>g.from_currency===r.from_currency&&g.to_currency===r.to_currency).sort((a,b)=>new Date(b.effective_date)-new Date(a.effective_date))[0];
              return(
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <span style={{fontSize:16,fontWeight:700}}>{fmtRate(r.rate)}</span>
                  {globalMatch&&parseFloat(r.rate)!==parseFloat(globalMatch.rate)&&(
                    <span style={{fontSize:10,color:'var(--warning)',background:'rgba(255,165,0,0.1)',padding:'1px 6px',borderRadius:4}}>
                      {t('currencies.branchRateOverride')}
                    </span>
                  )}
                </div>
              );
            }},
            {key:'effective_date',label:t('currencies.effectiveDate'),render:r=><span style={{fontSize:12}}>{fmtDate(r.effective_date)}</span>},
            {key:'notes',label:t('currencies.notes'),render:r=><span style={{color:'var(--td)',fontSize:12}}>{r.notes||'—'}</span>},
            {key:'creator',label:t('currencies.columns.createdBy'),render:r=><span style={{fontSize:12,color:'var(--td)'}}>{r.creator?`${r.creator.first_name} ${r.creator.last_name}`:'—'}</span>},
            {key:'is_active',label:t('currencies.columns.status'),render:r=><Badge variant={r.is_active?'success':'danger'}>{r.is_active?t('common.active'):t('common.inactive')}</Badge>},
          ]} data={filteredBranchRates} actions={canManage?r=>(
            <div style={{display:'flex',gap:6}}>
              <button className="btn btn-ghost btn-sm" onClick={()=>setBranchRateModal(r)}><Edit size={13}/></button>
              <button className="btn btn-danger btn-sm" onClick={()=>setDeleteTarget({type:'branch_rate',id:r.id})}><Trash2 size={13}/></button>
            </div>
          ):undefined}/>
          {filteredBranchRates.length===0&&!ld&&<p className="text-dim" style={{textAlign:'center',padding:24}}>{t('currencies.noBranchRates')}</p>}

          {/* No permission notice */}
          {!canManage&&(
            <div style={{display:'flex',alignItems:'center',gap:8,padding:'10px 14px',background:'var(--s2)',borderRadius:8,border:'1px solid var(--border)',marginTop:12,fontSize:12,color:'var(--td)'}}>
              <Lock size={14}/>{t('currencies.noPermission')}
            </div>
          )}
        </div>
      )}

      {/* ── MODALS ── */}
      {/* Edit Currency */}
      <Modal open={!!currencyModal} onClose={()=>setCurrencyModal(null)} title={t('currencies.editCurrency')} width={420}>
        {currencyModal&&(
          <div>
            <div style={{padding:'10px 14px',background:'var(--s2)',borderRadius:8,marginBottom:14,display:'flex',gap:12,alignItems:'center'}}>
              <span style={{fontSize:28,fontWeight:700}}>{currencyModal.symbol}</span>
              <div><div style={{fontWeight:700,fontSize:15}}>{currencyModal.code}</div><div style={{fontSize:12,color:'var(--td)'}}>{currencyModal.name}</div></div>
            </div>
            <FormInput label={t('currencies.name')} value={currForm.name||''} onChange={v=>setCurrForm(f=>({...f,name:v}))}/>
            <FormInput label={t('currencies.symbol')} value={currForm.symbol||''} onChange={v=>setCurrForm(f=>({...f,symbol:v}))} placeholder="$, ៛, ฿"/>
            <FormInput label={t('currencies.isDefault')} value={currForm.is_default?'true':'false'} onChange={v=>setCurrForm(f=>({...f,is_default:v==='true'}))} options={[{value:'false',label:t('common.no')},{value:'true',label:t('common.yes')}]}/>
            <FormInput label={t('currencies.isActive')} value={currForm.is_active?'true':'false'} onChange={v=>setCurrForm(f=>({...f,is_active:v==='true'}))} options={[{value:'true',label:t('common.active')},{value:'false',label:t('common.inactive')}]}/>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setCurrencyModal(null)}>{t('common.cancel')}</button>
              <button className="btn btn-primary" onClick={saveCurrency}>{t('common.save')}</button>
            </div>
          </div>
        )}
      </Modal>

      {/* Global Rate Modal */}
      <RateModal
        open={!!rateModal}
        onClose={()=>setRateModal(null)}
        onSave={saveRate}
        currencies={currencies}
        initial={rateModal==='new'?null:rateModal}
        title={rateModal==='new'?t('currencies.newRate'):t('currencies.editRate')}
      />

      {/* Branch Rate Modal */}
      <BranchRateModal
        open={!!branchRateModal}
        onClose={()=>setBranchRateModal(null)}
        onSave={saveBranchRate}
        currencies={currencies}
        branches={branches}
        initial={branchRateModal==='new'?null:branchRateModal}
        title={branchRateModal==='new'?t('currencies.newBranchRate'):t('currencies.editBranchRate')}
        isOwner={isOwner}
        myBranchId={myBranchId}
      />

      {/* Delete Confirm */}
      <Modal open={!!deleteTarget} onClose={()=>setDeleteTarget(null)} title={t('currencies.confirmDelete')} width={380}>
        <p style={{color:'var(--td)',marginBottom:18}}>{t('common.deleteConfirm')}</p>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={()=>setDeleteTarget(null)}>{t('common.cancel')}</button>
          <button className="btn btn-danger" onClick={confirmDelete}>{t('common.delete')}</button>
        </div>
      </Modal>
    </div>
  );
}
